import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import {
    getRoundName,
} from '../../../../../utils/courtSchedular';
import { getLabelByTypeId } from '../../../../../utils/types';
import {
    MatchSchedularStatusTypes,
    MatchStatusTypes,
    formatElapsedTime,
    getElapsedTime,
    shouldDisplayElapsedTimes,
} from '../../../../../utils/nextAvailable';

import styles from './nextAvailableLiveMatches.scss';
import { getFacilityAndCourt } from '../../../../../utils/events';
import Player from './Player';
import Img from '../../../../../components/Img';
import { sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';
import { EventProfileTabIdentifiers } from '../../../../../utils/constants';
import * as eventProfileActions from '../../../../../actions/event-profile-actions';

function LiveMatch(props) {
    const {
        match,
        locations,
        eventSchedule,
        utrTypeId,
        sportTypeId,
        filterParams,
        handleTabClick,
        eventId,
        drawsDict,
        drawsTabState,
    } = props;
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const dispatch = useDispatch();

    const {
        division: {
            tournament: {
                scoringTypes,
            },
        },
    } = coreUI.types.tms;

    const [elapsedTime, setElapsedTime] = useState({
        shouldDisplay: false,
        value: '0 min',
        hasCrossedAllottedTime: false,
    });

    const { facility, court } = getFacilityAndCourt(locations.data, match.courtId, match.drawClubLocationFacilityId);

    const scoringType = getLabelByTypeId(scoringTypes, match.scoringTypeId);

    const timeStringToDisplay = useMemo(() => {
        if (match.drawMatchStatus === MatchStatusTypes.ON_COURT) {
            if (match.schedulerStatus) {
                return moment.utc(match.dateTimeStarted).tz(eventSchedule?.timeZone?.value).format('h:mm a');
            }

            return '(starting soon)';
        }

        if (match.drawMatchStatus === MatchStatusTypes.IN_QUEUE) {
            return moment.utc(match.dateTimeInQueue).tz(eventSchedule?.timeZone?.value).format('h:mm a');
        }

        return null;
    }, [match.schedulerStatus, match.drawMatchStatus, eventSchedule]);

    const timeAndElapsedTimeString = useMemo(() => {
        let value = '';

        if (timeStringToDisplay) {
            value = `${timeStringToDisplay}`;
        }

        if (elapsedTime.shouldDisplay) {
            value += ` • ${formatElapsedTime(elapsedTime.value)} elapsed`;
        }

        return value;
    }, [timeStringToDisplay, elapsedTime]);

    const facilityAndCourtNames = [
        facility?.name,
    ];
    if (filterParams.matchStatus.id !== 3) {
        facilityAndCourtNames.push(court?.name);
    }
    const facilityAndCourtString = facilityAndCourtNames.filter((s) => s?.length > 0).join(', ');

    const updateElapsedTimes = useCallback(() => {
        const time = getElapsedTime(match);
        setElapsedTime({
            shouldDisplay: shouldDisplayElapsedTimes(match),
            value: time,
            hasCrossedAllottedTime: time > match.allottedMatchTime,
        });
    }, [match]);

    useEffect(() => {
        let interval;
        if (match.drawMatchStatus === MatchStatusTypes.ON_COURT || match.drawMatchStatus === MatchStatusTypes.DONE) {
            updateElapsedTimes();

            if (match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS) {
                interval = setInterval(() => {
                    updateElapsedTimes();
                }, 60000);
            }
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [match.drawMatchStatus, match.schedulerStatus, updateElapsedTimes]);

    const handleDrawClick = (e, match) => {
        const drawsTabId = EventProfileTabIdentifiers.DRAWS;
        e.stopPropagation();
        e.preventDefault();
        const drawToLoad = drawsDict[match.drawId];
        // load draws before redirect
        if (!drawToLoad.parentDrawId) {
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.id, setAsCurrentDraw: true }));
        } else {
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.parentDrawId, dontAutoLoadFirstChildDraw: true }));
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.id, isChildDraw: true }));
        }
        const drawsTabStateUpdate = { ...drawsTabState };
        drawsTabStateUpdate.activeRound = match.roundNumber - 1;
        dispatch(eventProfileActions.setDrawsTabState(drawsTabStateUpdate));
        handleTabClick({ tabId: drawsTabId, drawId: match.parentDrawId });
    };

    return (
        <>
            <div className={styles.liveMatch}>
                <div className={styles.match}>
                    <div className={`${styles.drawName}`} onClick={(e) => handleDrawClick(e, match)} data-testid={`event-profile.live-matches.match-${match.id}.draw-name`}>
                        {`${match.drawName}, ${getRoundName(match)}`}
                    </div>
                    <div className={`${styles.parentDrawName}`} data-testid={`event-profile.live-matches.match-${match.id}.parent-draw-name`}>
                        {match.parentDrawName}
                    </div>
                    <div className={styles.description}>
                        <span
                            className={`${styles.matchStatusIndicator} ${match.drawMatchStatus === MatchStatusTypes.IN_QUEUE ? styles.waiting : ''}`}
                        />
                        <span
                            className={styles.matchStatus}
                            data-testid={`event-profile.live-matches.match-${match.id}.status`}
                        >
                            {match.drawMatchStatus === MatchStatusTypes.IN_QUEUE ? 'In queue' : 'On Court'}
                        </span>
                        {timeAndElapsedTimeString && (
                            <span data-testid={`event-profile.live-matches.match-${match.id}.time`}>
                                {timeAndElapsedTimeString}
                            </span>
                        )}
                    </div>
                </div>
                <div className={styles.scoringFormat} data-testid={`event-profile.live-matches.match-${match.id}.scoring-format`}>
                    {scoringType}
                </div>
                {filterParams.matchStatus.id !== 3 && (
                    <div className={styles.court}>
                        {facility && (
                            <div className={styles.facilityName} data-testid={`event-profile.live-matches.match-${match.id}.facility-name`}>
                                {facility?.name}
                            </div>
                        )}
                        {court && (
                            <div className={styles.courtName} data-testid={`event-profile.live-matches.match-${match.id}.court-name`}>
                                {court?.name}
                            </div>
                        )}
                    </div>
                )}
                <div className={styles.playerOrTeam} data-testid={`event-profile.live-matches.match-${match.id}.position1-players`}>
                    {match.position1Players.map((player) => {
                        return (
                            <Player
                                key={`match-${match.id}-${player.id}`}
                                player={player}
                                sportTypeId={sportTypeId}
                                utrTypeId={utrTypeId}
                                formatTypeId={match.drawFormatTypeId}
                            />
                        );
                    })}
                </div>
                <div className={styles.playerOrTeam} data-testid={`event-profile.live-matches.match-${match.id}.position2-players`}>
                    {match.position2Players.map((player) => {
                        return (
                            <Player
                                key={`match-${match.id}-${player.id}`}
                                player={player}
                                sportTypeId={sportTypeId}
                                utrTypeId={utrTypeId}
                                formatTypeId={match.drawFormatTypeId}
                            />
                        );
                    })}
                </div>
            </div>
            <div className={styles.liveMatchMobile}>
                <div className={`${styles.matchStatusAndTime} ${match.drawMatchStatus === MatchStatusTypes.IN_QUEUE ? styles.waiting : ''}`} data-testid={`event-profile.live-matches.match-${match.id}.mobile-status`}>
                    <span>
                        {match.drawMatchStatus === MatchStatusTypes.IN_QUEUE ? 'In queue' : 'On Court'}
                    </span>
                    {timeAndElapsedTimeString && (
                        <span>
                            {timeAndElapsedTimeString}
                        </span>
                    )}
                </div>
                <div className={styles.drawName} onClick={(e) => handleDrawClick(e, match)} data-testid={`event-profile.live-matches.match-${match.id}.mobile-draw-name`}>
                    {`${match.drawName}, ${getRoundName(match)}`}
                </div>
                {match.parentDrawName && (
                    <div className="d-flex aic">
                        <Img src="/img/v3/icons/icon-14px-parent-draw.svg" className="mr4" />
                        <div className="flex-1" data-testid={`event-profile.live-matches.match-${match.id}.mobile-parent-draw-name`}>
                            {match.parentDrawName}
                        </div>
                    </div>
                )}
                {scoringType && (
                    <div className="d-flex aic">
                        <Img src="/img/v3/icons/icon-14px-scoring-type.svg" className="mr4" />
                        <div className="flex-1" data-testid={`event-profile.live-matches.match-${match.id}.mobile-scoring-format`}>
                            {scoringType}
                        </div>
                    </div>
                )}
                {facilityAndCourtString && (
                    <div className="d-flex aic">
                        <Img src="/img/v3/icons/icon-14px-facility.svg" className="mr4" />
                        <div className="flex-1" data-testid={`event-profile.live-matches.match-${match.id}.mobile-facility`}>
                            {facilityAndCourtString}
                        </div>
                    </div>
                )}
                {match.position1Players.map((player) => {
                    return (
                        <Player
                            key={`match-mobile-${match.id}-${player.id}`}
                            player={player}
                            sportTypeId={sportTypeId}
                            utrTypeId={utrTypeId}
                            formatTypeId={match.drawFormatTypeId}
                        />
                    );
                })}
                {match.position2Players.map((player) => {
                        return (
                            <Player
                                key={`match-mobile-${match.id}-${player.id}`}
                                player={player}
                                sportTypeId={sportTypeId}
                                utrTypeId={utrTypeId}
                                formatTypeId={match.drawFormatTypeId}
                            />
                        );
                    })}
            </div>
        </>
    );
}

LiveMatch.propTypes = {
    match: PropTypes.objectOf(any).isRequired,
    locations: PropTypes.objectOf(any).isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    utrTypeId: PropTypes.number,
    sportTypeId: PropTypes.number,
    filterParams: PropTypes.objectOf(any).isRequired,
    handleTabClick: PropTypes.func,
    eventId: PropTypes.number.isRequired,
    drawsDict: PropTypes.objectOf(any),
};

LiveMatch.defaultProps = {
    utrTypeId: utrTypeIds.MYUTR,
    sportTypeId: sportTypeIds.TENNIS,
    handleTabClick: () => { },
    drawsDict: {},
};

export default LiveMatch;
